import * as React from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import {
  LayoutDashboard,
  FileText,
  Calendar,
  Mail,
  MessageSquare,
  ImageIcon,
  CreditCard,
  Settings,
  Building2,
  Users,
  ScrollText,
  ShieldCheck,
  Rocket,
  TrendingUp,
  Palette,
  Layers,
  Newspaper,
} from 'lucide-react';

import type { Role, TenantFeatureKey, TenantFeatures } from '@/lib/auth';
import {
  Sidebar as SidebarPrimitive,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Roles allowed to see this entry. Operator sees everything. */
  roles?: Role[];
  /**
   * Mothball flag — entry is hidden unless `tenant.features[<key>]` is
   * true. Operator role bypasses this gate. Default-hidden until a
   * paying customer asks for the feature by name.
   */
  featureFlag?: TenantFeatureKey;
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

const CLINIC_NAV: NavGroup[] = [
  {
    title: 'Przegląd',
    items: [{ to: '/', label: 'Pulpit', icon: LayoutDashboard }],
  },
  {
    title: 'Strona',
    items: [
      { to: '/brief', label: 'Treść strony', icon: FileText },
      { to: '/strony', label: 'Strony', icon: Layers },
      { to: '/tresci/aktualnosci', label: 'Aktualności i realizacje', icon: Newspaper },
      { to: '/wyglad', label: 'Wygląd strony', icon: Palette },
      { to: '/media', label: 'Media', icon: ImageIcon },
      { to: '/publish', label: 'Publikacja', icon: Rocket },
    ],
  },
  {
    title: 'Pacjenci',
    items: [
      // booking surface defaults to Booksy/ZnL iframe — native in-house
      // booking re-enables per-tenant via the `in_house_booking` flag.
      { to: '/calendar', label: 'Kalendarz wizyt', icon: Calendar, featureFlag: 'in_house_booking' },
      { to: '/coordinate', label: 'Koordynacja lekarzy', icon: Calendar, featureFlag: 'multi_doctor' },
      { to: '/messages', label: 'Wiadomości', icon: Mail },
      { to: '/sms', label: 'SMS', icon: MessageSquare, featureFlag: 'sms' },
    ],
  },
  {
    title: 'Wzrost',
    items: [
      { to: '/growth', label: 'Content marketplace', icon: TrendingUp, featureFlag: 'content_marketplace' },
    ],
  },
  {
    title: 'Konto',
    items: [
      { to: '/team', label: 'Zespół (dostęp)', icon: Users, featureFlag: 'rbac' },
      { to: '/billing', label: 'Subskrypcja', icon: CreditCard },
      { to: '/settings', label: 'Ustawienia', icon: Settings },
      { to: '/help', label: 'Pomoc / kontakt', icon: MessageSquare },
    ],
  },
];

const OPERATOR_NAV: NavGroup[] = [
  {
    title: 'Operacyjne',
    items: [
      { to: '/operator', label: 'Pulpit', icon: LayoutDashboard },
      { to: '/operator/funnel', label: 'Funnel', icon: TrendingUp },
      { to: '/operator/sales', label: 'Sales', icon: Building2 },
      { to: '/inbox', label: 'Inbox (max@)', icon: Mail },
      { to: '/operator/tenants', label: 'Klienci', icon: Building2 },
      { to: '/operator/content-orders', label: 'Zamówienia content', icon: TrendingUp },
      { to: '/operator/conversations', label: 'Konwersacje agenta', icon: MessageSquare },
      { to: '/operator/users', label: 'Użytkownicy', icon: Users },
    ],
  },
  {
    title: 'Treść',
    items: [
      { to: '/operator/services', label: 'Biblioteka usług', icon: FileText },
      { to: '/operator/forbidden', label: 'Forbidden claims', icon: ShieldCheck },
    ],
  },
  {
    title: 'Audyt',
    items: [{ to: '/operator/audit', label: 'Audit log', icon: ScrollText }],
  },
];

export interface SidebarProps {
  role: Role;
  brandLabel: string;
  brandSublabel?: string;
  /**
   * Per-tenant feature flags. Filters mothballed nav entries. Pass
   * `undefined` to default everything off (matches a fresh tenant).
   * Operator role ignores this gate entirely.
   */
  features?: TenantFeatures;
}

export function Sidebar({ role, brandLabel, brandSublabel, features }: SidebarProps) {
  const isOperator = role === 'operator';
  const rawGroups = isOperator ? OPERATOR_NAV : CLINIC_NAV;
  // Filter mothball-gated items for clinic role; operator sees everything.
  const groups = isOperator
    ? rawGroups
    : rawGroups
        .map((g) => ({
          ...g,
          items: g.items.filter((it) => {
            if (!it.featureFlag) return true;
            return features?.[it.featureFlag] === true;
          }),
        }))
        .filter((g) => g.items.length > 0);
  return (
    <SidebarPrimitive>
      <SidebarHeader>
        <Brand label={brandLabel} sublabel={brandSublabel} />
      </SidebarHeader>
      <SidebarContent>
        {groups.map((group) => (
          <SidebarGroup key={group.title}>
            <SidebarGroupLabel>{group.title}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.map((item) => (
                  <SidebarMenuNavItem key={item.to} item={item} />
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>
    </SidebarPrimitive>
  );
}

/**
 * Brand header — adapts to collapsed state: shows only the first letter
 * of the brand inside a square chip when collapsed (icon-only rail).
 */
function Brand({ label, sublabel }: { label: string; sublabel?: string }) {
  const { state, isMobile } = useSidebar();
  const collapsed = state === 'collapsed' && !isMobile;
  const initial = label.trim()[0]?.toUpperCase() ?? '·';

  if (collapsed) {
    return (
      <div className="flex items-center justify-center px-1 py-1">
        <span className="grid size-8 place-items-center rounded-md bg-bg font-display text-sm font-semibold text-text ring-1 ring-black/5">
          {initial}
        </span>
      </div>
    );
  }

  return (
    <div className="px-1 py-1">
      <p className="truncate font-display text-base font-semibold text-text">{label}</p>
      {sublabel ? (
        <p className="mt-0.5 truncate text-xs text-text-subtle">{sublabel}</p>
      ) : null}
    </div>
  );
}

function SidebarMenuNavItem({ item }: { item: NavItem }) {
  const { state, isMobile } = useSidebar();
  const matches = useRouterState({ select: (s) => s.matches });
  const active = matches.some((m) => m.pathname === item.to);
  const collapsed = state === 'collapsed' && !isMobile;
  const Icon = item.icon;

  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={active} tooltip={item.label}>
        <Link to={item.to as never} className={collapsed ? 'justify-center px-0' : undefined}>
          <Icon className="size-4 shrink-0" aria-hidden="true" />
          {collapsed ? null : <span className="truncate">{item.label}</span>}
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}
